export type ConversationEventType = 'transcript' | 'chat' | 'userQuery';

export interface TranscriptTurn {
  id: string;
  eventType: ConversationEventType;
  speaker: string;
  text: string;
  timestamp: string | null;
  startSeconds: number | null;
  endSeconds: number | null;
  dialogueAct: string | null;
  labels: string[];
}

export interface TranscriptDocument {
  meetingId: string | null;
  turns: TranscriptTurn[];
}

const INVALID_JSON_MESSAGE = 'The selected file is not valid JSON.';
const INVALID_TRANSCRIPT_MESSAGE =
  'The selected file does not contain a supported transcript.';
const TEXT_TURN_PATTERN = /^\s*(?:\[([^\]]+)\]\s*)?([^:]+):\s*(.+)\s*$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function optionalString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : null;
}

function optionalSeconds(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? value
    : null;
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter(
        (item): item is string =>
          typeof item === 'string' && item.trim().length > 0,
      )
    : [];
}

function parseJsonTurn(value: unknown, index: number): TranscriptTurn {
  if (!isRecord(value)) {
    throw new Error(INVALID_TRANSCRIPT_MESSAGE);
  }

  const speaker = optionalString(value.speaker);
  const text = optionalString(value.text);
  if (speaker === null || text === null) {
    throw new Error(INVALID_TRANSCRIPT_MESSAGE);
  }

  return {
    id: `turn-${index}`,
    eventType: 'transcript',
    speaker,
    text,
    timestamp: null,
    startSeconds: optionalSeconds(value.start),
    endSeconds: optionalSeconds(value.end),
    dialogueAct: optionalString(value.dialogue_act),
    labels: stringArray(value.label),
  };
}

function parseJsonTranscript(source: string): TranscriptDocument {
  let parsed: unknown;
  try {
    parsed = JSON.parse(source);
  } catch {
    throw new Error(INVALID_JSON_MESSAGE);
  }

  if (!isRecord(parsed) || !Array.isArray(parsed.turns)) {
    throw new Error(INVALID_TRANSCRIPT_MESSAGE);
  }

  const turns = parsed.turns.map(parseJsonTurn);
  if (turns.length === 0) {
    throw new Error(INVALID_TRANSCRIPT_MESSAGE);
  }

  return {
    meetingId: optionalString(parsed.meeting_id),
    turns,
  };
}

function parseTextTranscript(source: string): TranscriptDocument {
  const turns: TranscriptTurn[] = [];

  for (const line of source.split(/\r?\n/)) {
    if (line.trim().length === 0) {
      continue;
    }

    const match = TEXT_TURN_PATTERN.exec(line);
    if (match === null) {
      throw new Error(INVALID_TRANSCRIPT_MESSAGE);
    }

    const dialogueAct = optionalString(match[1]);
    const speaker = match[2]?.trim();
    const text = match[3]?.trim();
    if (!speaker || !text) {
      throw new Error(INVALID_TRANSCRIPT_MESSAGE);
    }

    turns.push({
      id: `turn-${turns.length}`,
      eventType: 'transcript',
      speaker,
      text,
      timestamp: null,
      startSeconds: null,
      endSeconds: null,
      dialogueAct,
      labels: [],
    });
  }

  if (turns.length === 0) {
    throw new Error(INVALID_TRANSCRIPT_MESSAGE);
  }

  return { meetingId: null, turns };
}

export function parseTranscript(
  source: string,
  fileName = '',
): TranscriptDocument {
  const trimmedSource = source.trim();
  const normalizedFileName = fileName.trim().toLowerCase();
  const isJson =
    normalizedFileName.endsWith('.json') ||
    (!normalizedFileName.endsWith('.txt') && trimmedSource.startsWith('{'));

  return isJson
    ? parseJsonTranscript(trimmedSource)
    : parseTextTranscript(trimmedSource);
}
